// Thin client around the 2 personal-health workers behind APP_BEARER_TOKEN.
// Token lives in localStorage (single-user app behind Cloudflare Access — the
// Access policy is the actual security boundary; this token is just so the
// workers don't need their own auth flow).

const TOKEN_KEY = "personal-health.token";
const REPORT_BASE_KEY = "personal-health.reportBase";
const ASK_BASE_KEY = "personal-health.askBase";

export type WorkerEndpoints = {
  reportBase: string;
  askBase: string;
};

const DEFAULT_ENDPOINTS: WorkerEndpoints = {
  reportBase: "https://personal-health-daily-report.duytrieu5693.workers.dev",
  askBase: "https://personal-health-multi-llm.duytrieu5693.workers.dev"
};

export function getEndpoints(): WorkerEndpoints {
  return {
    reportBase: localStorage.getItem(REPORT_BASE_KEY) ?? DEFAULT_ENDPOINTS.reportBase,
    askBase: localStorage.getItem(ASK_BASE_KEY) ?? DEFAULT_ENDPOINTS.askBase
  };
}

export function setEndpoints(e: WorkerEndpoints): void {
  localStorage.setItem(REPORT_BASE_KEY, e.reportBase);
  localStorage.setItem(ASK_BASE_KEY, e.askBase);
}

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? "";
}

export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t);
}

async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init?.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  return fetch(url, { ...init, headers });
}

export type DailyReport = {
  date_local: string;
  status: "normal" | "note" | "attention" | "urgent";
  headline: string;
  body_md: string;
  generated_at: number;
  model: string;
};

export type DailyReportSummary = Pick<DailyReport, "date_local" | "status" | "headline" | "generated_at">;

export async function fetchReport(date: string): Promise<DailyReport | null> {
  const { reportBase } = getEndpoints();
  const res = await authedFetch(`${reportBase}/report/${date}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`fetchReport ${res.status}`);
  const json = (await res.json()) as { found: boolean } & DailyReport;
  return json.found ? json : null;
}

export async function fetchRecentReports(limit = 14): Promise<DailyReportSummary[]> {
  const { reportBase } = getEndpoints();
  const res = await authedFetch(`${reportBase}/reports?limit=${limit}`);
  if (!res.ok) throw new Error(`fetchRecentReports ${res.status}`);
  const json = (await res.json()) as { reports: DailyReportSummary[] };
  return json.reports;
}

export async function generateReport(date: string, force = false): Promise<DailyReport | null> {
  const { reportBase } = getEndpoints();
  const params = new URLSearchParams({ date });
  if (force) params.set("force", "1");
  const res = await authedFetch(`${reportBase}/generate?${params}`, { method: "POST" });
  if (!res.ok) throw new Error(`generateReport ${res.status}`);
  const json = (await res.json()) as { generated: boolean; report: DailyReport | null };
  return json.report;
}

export type AskMode = "single" | "compare";

export type AskAnswer = {
  provider: string;
  model: string;
  content: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  error: string | null;
  created_at: number;
};

export type AskSession = {
  id: string;
  question: string;
  mode: AskMode;
  status: "pending" | "partial" | "complete" | "failed";
  context_date: string | null;
  created_at: number;
  completed_at: number | null;
};

export async function startAsk(args: {
  question: string;
  mode: AskMode;
  withContext: boolean;
}): Promise<{ sessionId: string }> {
  const { askBase } = getEndpoints();
  const res = await authedFetch(`${askBase}/ask`, {
    method: "POST",
    body: JSON.stringify(args)
  });
  if (!res.ok) throw new Error(`startAsk ${res.status}`);
  const json = (await res.json()) as { sessionId: string };
  return json;
}

export async function fetchSession(
  id: string
): Promise<{ session: AskSession; answers: AskAnswer[] }> {
  const { askBase } = getEndpoints();
  const res = await authedFetch(`${askBase}/session/${id}`);
  if (!res.ok) throw new Error(`fetchSession ${res.status}`);
  return (await res.json()) as { session: AskSession; answers: AskAnswer[] };
}

export async function synthesize(sessionId: string): Promise<{ content: string }> {
  const { askBase } = getEndpoints();
  const res = await authedFetch(`${askBase}/synthesize`, {
    method: "POST",
    body: JSON.stringify({ sessionId })
  });
  if (!res.ok) throw new Error(`synthesize ${res.status}`);
  return (await res.json()) as { content: string };
}

export async function listSessions(limit = 20): Promise<AskSession[]> {
  const { askBase } = getEndpoints();
  const res = await authedFetch(`${askBase}/sessions?limit=${limit}`);
  if (!res.ok) throw new Error(`listSessions ${res.status}`);
  const json = (await res.json()) as { sessions: AskSession[] };
  return json.sessions;
}
